import { z } from "zod";
import { IsoDateTime, OperatorEnvironment, PlayerRef } from "./common.ts";

/**
 * Block reasons (spec player-blocklist): a namespace of their own, never a verification reason code
 * (`jurisdiction_breach` is not `outside_jurisdiction`).
 */
export const BLOCK_REASONS = [
  "fraud",
  "multi_account",
  "jurisdiction_breach",
  "self_exclusion",
  "regulatory_request",
  "other",
] as const;
export const BlockReason = z.enum(BLOCK_REASONS).meta({ id: "BlockReason" });
export type BlockReason = z.infer<typeof BlockReason>;

export const BlockTargetKind = z.enum(["player", "device"]).meta({ id: "BlockTargetKind" });
export type BlockTargetKind = z.infer<typeof BlockTargetKind>;

/** Who changed a block: a console user (`iam` id) or the expiry job (`system`). */
export const BlockActor = z.discriminatedUnion("type", [
  z.object({ type: z.literal("console_user"), id: z.uuid() }).strict(),
  z.object({ type: z.literal("system"), id: z.literal("system") }).strict(),
]);
export type BlockActor = z.infer<typeof BlockActor>;

/** Unblock reason typed by the analyst (manual unblock only); the block comment never leaves `explorer`. */
export const UnblockReasonText = z.string().trim().min(1).max(500);

const base = {
  event_id: z.uuid(),
  occurred_at: IsoDateTime,
  block_id: z.uuid(),
  operator_id: z.uuid(),
  environment: OperatorEnvironment,
};
const playerTarget = { target_kind: z.literal("player"), target_ref: PlayerRef };
const deviceTarget = { target_kind: z.literal("device"), target_ref: z.uuid() };
const blocked = {
  reason: BlockReason,
  /** Absent for a block without expiry. */
  expires_at: IsoDateTime.optional(),
  actor: z.object({ type: z.literal("console_user"), id: z.uuid() }).strict(),
};
const manualUnblock = {
  cause: z.literal("manual"),
  unblock_reason: UnblockReasonText,
  actor: z.object({ type: z.literal("console_user"), id: z.uuid() }).strict(),
};
const expiredUnblock = {
  cause: z.literal("expired"),
  /** The expiry that lifted the block. */
  expires_at: IsoDateTime,
  actor: z.object({ type: z.literal("system"), id: z.literal("system") }).strict(),
};

/**
 * Block integration events v1 (published by the Decision Engine through `core.outbox` for its List
 * Entries since split-core-into-trust-contexts task 7.1, formerly by `explorer`; no consumer since
 * task 7.2 removed `core.block_projection`; ADR 0001 R3, R5). Strict: a comment or any unknown field
 * is rejected, so free text written with a block never reaches another module.
 */
export const PlayerBlockedV1 = z
  .object({ ...base, ...playerTarget, ...blocked })
  .strict()
  .meta({ id: "PlayerBlockedV1" });
export type PlayerBlockedV1 = z.infer<typeof PlayerBlockedV1>;

export const DeviceBlockedV1 = z
  .object({ ...base, ...deviceTarget, ...blocked })
  .strict()
  .meta({ id: "DeviceBlockedV1" });
export type DeviceBlockedV1 = z.infer<typeof DeviceBlockedV1>;

export const PlayerUnblockedV1 = z
  .discriminatedUnion("cause", [
    z.object({ ...base, ...playerTarget, ...manualUnblock }).strict(),
    z.object({ ...base, ...playerTarget, ...expiredUnblock }).strict(),
  ])
  .meta({ id: "PlayerUnblockedV1" });
export type PlayerUnblockedV1 = z.infer<typeof PlayerUnblockedV1>;

export const DeviceUnblockedV1 = z
  .discriminatedUnion("cause", [
    z.object({ ...base, ...deviceTarget, ...manualUnblock }).strict(),
    z.object({ ...base, ...deviceTarget, ...expiredUnblock }).strict(),
  ])
  .meta({ id: "DeviceUnblockedV1" });
export type DeviceUnblockedV1 = z.infer<typeof DeviceUnblockedV1>;

/** Event type names and version as written to the outbox (`event_type`, `event_version`). */
export const BLOCK_EVENT_TYPES = {
  PlayerBlocked: PlayerBlockedV1,
  PlayerUnblocked: PlayerUnblockedV1,
  DeviceBlocked: DeviceBlockedV1,
  DeviceUnblocked: DeviceUnblockedV1,
} as const;
export const BLOCK_EVENT_VERSION = 1;
export type BlockEventType = keyof typeof BLOCK_EVENT_TYPES;
